import { useDatePicker } from 'react-aria';
import { useDatePickerState } from 'react-stately';
import React from 'react';
import type { CalendarDate, DateValue } from '@internationalized/date';
import Calendar from './calendar';
import Button from '../button';
import DateField from './date-field';
import DropdownMenu from '../dropdown-menu';
import FormLabel from '../form-label';
import throwOnMissing from '../utils/throw-on-missing';
import CalendarIcon from '../../../images/svg/calendar.svg';
import { colors } from '../colors';
import FormAlert from '../form-alert';
import useFormState from '../form-provider/use-form-state';

type DatePickerProps = {
    name: string
    placeholder?: string
    required?: boolean;
    minValue?: CalendarDate
    maxValue?: CalendarDate
    onChange?: (value: DateValue|null) => void
};
const triggerButtonStyles = {
    outline: 'none',
    padding: 11,
    backgroundColor: colors.grey200,
    borderRadius: '0 3px 3px 0',
    '&:hover': {
        backgroundColor: colors.grey100,
    },
    '&[data-state="open"]': {
        backgroundColor: colors.grey200,
    },
};

const DatePicker = ({
    name,
    placeholder,
    required,
    onChange,
    ...props
}: DatePickerProps): JSX.Element => {
    throwOnMissing(name, 'name', 'DatePicker');

    const ref = React.useRef<HTMLDivElement>(null);

    const openButtonRef = React.useRef<HTMLButtonElement>(null);

    const formContext = useFormState<string>({
        name,
        validators: {
            required,
            custom: () => {
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                if (state) return state.validationState !== 'invalid';
                return false;
            },
        },
    });

    const state = useDatePickerState({
        ...props,
        onChange: (value) => {
            // value.toString() is like toISOString() but without time
            // Might need this: new Date(value.toString()).toISOString()
            if (value) {
                formContext.setValue(value.toString());
                if (onChange) {
                    onChange(value);
                }
            } else {
                formContext.setValue(null);
                if (onChange) {
                    onChange(null);
                }
            }
        },
    });

    const {
        groupProps,
        fieldProps,
        buttonProps,
        calendarProps,
    } = useDatePicker(props, state, ref);

    return (
        <>
            <div
                ref={ref}
                {...groupProps}
                css={{
                    width: '100%',
                    '&[aria-expanded="true"] ~ div label, , &[data-value="true"] ~ div label': {
                        opacity: '1',
                    },
                    '&[aria-expanded="true"], &[data-value="true"]': {
                        backgroundColor: 'red',
                        border: '2px solid var(--color-grey-200)',
                    },
                    border: formContext.error ? `2px solid ${colors.statesError}` : `2px solid ${colors.grey200}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    userSelect: 'none',
                    // paddingLeft: '12px',
                    // width: '100%',
                    fontSize: '16px',
                    backgroundColor: formContext.value ? colors.shadesWhite : colors.grey200,
                    color: 'var(--color-shades-black)',
                    borderRadius: '4px',
                    textAlign: 'left',
                    gap: '4px',
                    '@media (max-width: 767px)': {
                        fontSize: '14px',
                    },
                }}
            >
                <FormLabel id="whatever">Label</FormLabel>
                <DateField {...fieldProps} />
                <DropdownMenu
                    dropdownProps={{
                        root: {
                            open: state.isOpen,
                        },
                        content: {
                            onEscapeKeyDown: () => state.close(),
                            onInteractOutside: () => state.close(),
                        },
                        trigger: {
                            asChild: true,
                        },
                    }}
                    css={triggerButtonStyles}
                    // the jsx below is ugly but only way I was able to have
                    // the above "onInteractOutside" close the dropdown
                    // without the button itself opening it again
                    // using Button on both didn't work
                    title={state.isOpen ? (
                        <button type="button" {...buttonProps} onClick={() => state.setOpen(false)}>
                            <CalendarIcon height={25} />
                        </button>
                    ) : (
                        <Button
                            {...buttonProps}
                            ref={openButtonRef}
                            onClick={(e) => {
                                e.preventDefault();
                                state.setOpen(true);
                            }}
                            onKeyDown={e => [' ', 'ArrowDown', 'Enter'].includes(e.key) && state.setOpen(true)}
                        >
                            <CalendarIcon height={25} />
                        </Button>
                    )}
                    align="end"
                >
                    <Calendar {...calendarProps} />
                </DropdownMenu>
            </div>
            {formContext.error && (
                <FormAlert>{formContext.error}</FormAlert>
            )}
        </>
    );
};

export default DatePicker;
